import React, { createContext, useContext, useRef, useState } from "react";
import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material";
import { ChordList } from "../Entities/ChordListModel";
import { SongModel } from "../Entities/SongModel";
import { apiGetSongByUrl, apiScrapeUrl } from "./useDb";
import { addSongToListDirect } from "./useChordLists";
import { useUser } from "./useUser";

type ImportJob = { key: string; url: string; list: ChordList; owner: string; status: "pending" | "failed" | "done"; message: string; song?: SongModel };
const ImportContext = createContext<{ enqueue: (url: string, list: ChordList) => void }>({ enqueue: () => {} });

export function normalizeSongUrl(value: string): string {
  const url = new URL(value.includes("://") ? value.trim() : `https://${value.trim()}`);
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) throw new Error("Enter a valid song URL.");
  const host = url.hostname.toLowerCase();
  if (!["tab4u.com", "ultimate-guitar.com"].some(domain => host === domain || host.endsWith(`.${domain}`))) throw new Error("Use a Tab4U or Ultimate Guitar song URL.");
  url.hash = "";
  return url.toString().replace(/\/$/, "");
}

export function UrlImportsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const current = useRef({ owner: user?.id || "guest" });
  current.current = { owner: user?.id || "guest" };
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const running = useRef(new Set<string>());
  const songs = useRef(new Map<string, Promise<SongModel>>());
  const update = (key: string, changes: Partial<ImportJob>) => setJobs(previous => previous.map(job => job.key === key ? { ...job, ...changes } : job));
  const run = async (job: ImportJob) => {
    if (running.current.has(job.key)) return;
    running.current.add(job.key);
    update(job.key, { status: "pending", message: `Adding song to ${job.list.name}…` });
    try {
      let song = job.song;
      if (!song) {
        let lookup = songs.current.get(job.url);
        if (!lookup) {
          lookup = (async () => {
            const existing = await apiGetSongByUrl(job.url);
            if (existing) return existing;
            update(job.key, { message: `Importing song for ${job.list.name}…` });
            return apiScrapeUrl(job.url);
          })();
          songs.current.set(job.url, lookup);
        }
        try { song = await lookup; } finally { songs.current.delete(job.url); }
        update(job.key, { song });
      }
      if (current.current.owner !== job.owner) throw new Error("Your account changed. Add this URL again in your current account.");
      const activeUserId = current.current.owner === "guest" ? null : current.current.owner;
      await addSongToListDirect(job.list.id, { song_id: song.id, song }, activeUserId);
      window.dispatchEvent(new Event("chordmaster_lists_changed"));
      update(job.key, { status: "done", message: `Added ${song.title} to ${job.list.name}.` });
    } catch (error: any) {
      update(job.key, { status: "failed", message: error.message || "Could not add song." });
    } finally { running.current.delete(job.key); }
  };
  const enqueue = (url: string, list: ChordList) => {
    const normalized = normalizeSongUrl(url);
    const key = `${current.current.owner}:${list.id}:${normalized}`;
    if (running.current.has(key)) return;
    const job: ImportJob = { key, url: normalized, list, owner: current.current.owner, status: "pending", message: `Adding song to ${list.name}…` };
    setJobs(previous => [...previous.filter(item => item.key !== key), job]);
    void run(job);
  };
  return <ImportContext.Provider value={{ enqueue }}>
    {children}
    {jobs.length > 0 && <Box sx={{ position: "fixed", bottom: 16, right: 16, left: { xs: 16, sm: "auto" }, width: { sm: 360 }, zIndex: 1400, maxHeight: "40vh", overflow: "auto" }}>
      {jobs.map(job => <Alert key={job.key} severity={job.status === "failed" ? "error" : job.status === "done" ? "success" : "info"} sx={{ mb: 1 }} onClose={job.status === "pending" ? undefined : () => setJobs(previous => previous.filter(item => item.key !== job.key))}>
        <Typography variant="body2">{job.message}</Typography>
        {job.status === "pending" && <><LinearProgress sx={{ my: 1 }} /><Typography variant="caption">You can keep browsing. Keep this tab open until it finishes.</Typography></>}
        {job.status === "failed" && <Button size="small" onClick={() => void run(job)}>Retry</Button>}
      </Alert>)}
    </Box>}
  </ImportContext.Provider>;
}

export const useUrlImports = () => useContext(ImportContext);
